/**
 * `TRACE_animation_binding` — clip binding by name, the Babylon half.
 *
 * For a SceneObject whose node carries `sources`, this builds one
 * AnimationGroup per (target, source, source animation) and hands them to the
 * object as clips, under the source animation's name. In order:
 *
 * 1. **Read the source** — bytes only. A source is used for its animations,
 *    never instantiated, so nothing of it renders, whether or not any node
 *    references it.
 * 2. **Key each channel by name** (`bindSourceByName` — pure): a node channel
 *    by its node's name, a weights channel by morph target name, a channel on
 *    the source's `root` node as a root offset.
 * 3. **Match** each key against the target's instance: the nodes the loader
 *    mounted under the target, whose file names the loader stamped
 *    (`loaders/gltf/TRACE_animation_binding.ts`) and whose glTF indices
 *    Babylon stamped (`utils/GltfPointers.ts`). Equal names bind; nothing
 *    else does. A morph target name binds on every mesh of the instance that
 *    has a target of that name.
 * 4. **Apply**: a matched property becomes an ordinary Babylon Animation on
 *    that node (TRS), on each primitive's MorphTarget (weights), or on the
 *    node's visibility. Root channels animate the object's animation root,
 *    which sits between the object's authored transform and its content —
 *    `M_authored · M_root(t)`. Rotations on nodes a `RestRelativeBinding`
 *    names are applied on top of the node's rest rotation.
 *
 * Names the instance lacks are normal partial binding (logged at debug);
 * channels that cannot bind for any other reason are reported ONCE per
 * binding. Bound groups are never started here: playback is the runtime's
 * (`Animation{clip}`, a Timeline). They are re-built whenever the object's
 * components are (a reload after a `src` change re-mounts the instance) and
 * disposed with the object.
 */

import { Animation } from "@babylonjs/core/Animations/animation";
import { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { AnimationKeyInterpolation, type IAnimationKey } from "@babylonjs/core/Animations/animationKey";
import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { MorphTarget } from "@babylonjs/core/Morph/morphTarget";
import type { Node } from "@babylonjs/core/node";
import type { SceneObject } from "../../core/SceneObject";
import type { RestRelativeBindingData } from "../../scenedata/behaviors/restrelativebindingdata";
import { registerInstanceWriteClaim } from "../../core/instanceWrites";
import type { ResourceManager } from "../ResourceManager";
import { getGltfPointers } from "../../utils/GltfPointers";
import { Logger } from "../../utils/Logger";
import { getGltfFileNames, getGltfPrimitiveNode } from "../gltf/TRACE_animation_binding";
import {
    bindSourceByName,
    loadGltfBuffers,
    readGltf,
    readSampler,
    resolveFileNames,
    type DecodedSampler,
    type FileNames,
    type GltfContents,
    type NameBoundAnimation,
    type NameChannel,
    type TrsPath,
} from "../formats/gltf21/animationBinding";

/** Babylon's glTF loader keys clips at 60 fps; bound clips do the same, so a bound clip and the same clip loaded inline agree frame for frame. */
export const BOUND_CLIP_FPS = 60;

/** Diagnostic code for channels that did not bind. */
export const ANIMATION_BINDING_UNBOUND = "animation-binding-unbound";

// A target with a binding writes its instance per placement, so its meshes
// must not be shared with other placements.
registerInstanceWriteClaim("TRACE_animation_binding", (object) => object.animationBinding !== undefined);

// =============================================================================
// Sources
// =============================================================================

/** A source file, read once. */
interface ParsedSource {
    json: Record<string, unknown>;
    names: FileNames;
    buffers: (Uint8Array | null)[];
}

// Only cache the self-contained document by Blob identity. Its companion
// buffers belong to the resource manager and source directory of each use.
const sourceCacheByBlob = new WeakMap<Blob, Promise<{ contents: GltfContents; names: FileNames }>>();
const sourceCacheByUrl = new WeakMap<ResourceManager, Map<string, Promise<ParsedSource>>>();

async function fetchBytes(url: string): Promise<Uint8Array> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
    return new Uint8Array(await response.arrayBuffer());
}

async function parseSource(bytes: Uint8Array, baseUrl: string | undefined): Promise<ParsedSource> {
    const contents = readGltf(bytes);
    const buffers = await loadGltfBuffers(contents, async (uri) => {
        if (!baseUrl) return null;
        try {
            return await fetchBytes(new URL(uri, baseUrl).href);
        } catch {
            return null;
        }
    });
    return { json: contents.json, names: resolveFileNames(contents.json), buffers };
}

/**
 * The source's bytes, from wherever a node `src` would get them: a blob the
 * ResourceManager holds (a bundle, an import), else the URL a folder-relative
 * path was registered against, else the URL itself. Parsed once per blob /
 * URL, so N placements of one recording read the performance once.
 */
async function loadSource(resources: ResourceManager, src: string): Promise<ParsedSource> {
    const blob = resources.getAsset(src);
    if (blob) {
        let cached = sourceCacheByBlob.get(blob);
        if (!cached) {
            cached = blob.arrayBuffer().then(buffer => {
                const contents = readGltf(new Uint8Array(buffer));
                return { contents, names: resolveFileNames(contents.json) };
            });
            sourceCacheByBlob.set(blob, cached);
            cached.catch(() => sourceCacheByBlob.delete(blob));
        }
        const { contents, names } = await cached;
        const sourceUrl = resources.getAssetSourceUrl(src) ?? resources.resolveAssetUrl(src)
            ?? (resources.isLocal(src) ? undefined : src);
        const baseUrl = resources.getAssetBaseUrl(src) ?? sourceUrl;
        const buffers = await loadGltfBuffers(contents, async uri => {
            // Resolve relative to the source first: two bundled directories
            // may both contain a perf.bin. The shared resolver also handles
            // URI escaping, dot segments and flattened upload companions.
            const absolute = /^(?:[a-z][a-z0-9+.-]*:|\/)/i.test(uri);
            const sibling = absolute ? resources.getAsset(uri)
                : resources.resolveExternalAssetBlob(src.slice(0, src.lastIndexOf('/') + 1) + uri)
                    ?? resources.resolveExternalAssetBlob(uri);
            if (sibling) return new Uint8Array(await sibling.arrayBuffer());
            try {
                return await fetchBytes(new URL(uri, baseUrl).href);
            } catch {
                return null;
            }
        });
        return { json: contents.json, names, buffers };
    }
    const url = resources.resolveAssetUrl(src) ?? (resources.isLocal(src) ? undefined : src);
    if (!url) return Promise.reject(new Error(`"${src}" is neither held locally nor resolvable to a URL`));
    let byUrl = sourceCacheByUrl.get(resources);
    if (!byUrl) {
        byUrl = new Map<string, Promise<ParsedSource>>();
        sourceCacheByUrl.set(resources, byUrl);
    }
    let cached = byUrl.get(url);
    if (!cached) {
        const map = byUrl;
        cached = fetchBytes(url).then(bytes => parseSource(bytes, url));
        map.set(url, cached);
        cached.catch(() => map.delete(url));
    }
    return cached;
}

// =============================================================================
// The target's instance
// =============================================================================

const NODE_POINTER = /^\/nodes\/(\d+)$/;

/** A mounted file instance: its names and the Babylon node of each glTF node index. */
export interface InstanceView {
    names: FileNames;
    nodes: Map<number, TransformNode>;
    /** The primitive meshes of each glTF node's mesh, wherever Babylon parented them (skinning moves them). */
    primitives: Map<number, AbstractMesh[]>;
}

/**
 * The instance mounted under `object`: every loader-created node beneath its
 * transform, stopping at child objects (they are other nodes of the document,
 * not part of this file). Null when nothing is mounted (no `externalAsset`,
 * not loaded, a nested DOCUMENT rather than a glTF file); an error when the
 * nodes come from more than one file.
 */
export function instanceView(object: SceneObject): InstanceView | null | { error: string } {
    const prune = new Set<Node>(object.getChildren().map(c => c.transform));
    const files = new Set<FileNames>();
    const nodes = new Map<number, TransformNode>();
    const primitives = new Map<number, AbstractMesh[]>();
    const stack: Node[] = [object.transform];
    while (stack.length > 0) {
        const node = stack.pop()!;
        if (node !== object.transform && prune.has(node)) continue;
        const names = getGltfFileNames(node);
        if (names) files.add(names);
        const owner = getGltfPrimitiveNode(node);
        if (owner !== undefined && node instanceof AbstractMesh) {
            const list = primitives.get(owner) ?? [];
            list.push(node);
            primitives.set(owner, list);
        }
        if (node instanceof TransformNode) {
            for (const pointer of getGltfPointers(node)) {
                const m = NODE_POINTER.exec(pointer);
                if (m && !nodes.has(Number(m[1]))) nodes.set(Number(m[1]), node);
            }
        }
        stack.push(...node.getChildren());
    }
    if (files.size === 0) return null;
    if (files.size > 1) return { error: `its instance holds nodes from ${files.size} files` };
    return { names: [...files][0], nodes, primitives };
}

/** The Babylon meshes of a glTF node's mesh — one per primitive (Babylon splits them). */
export function primitivesOf(node: TransformNode): AbstractMesh[] {
    const isPrimitive = (n: Node): boolean => getGltfPointers(n).some(p => p.startsWith("/meshes/"));
    const isGltfNode = (n: Node): boolean => getGltfPointers(n).some(p => p.startsWith("/nodes/"));
    const out: AbstractMesh[] = [];
    if (node instanceof AbstractMesh && isPrimitive(node)) out.push(node);
    for (const child of node.getChildren()) {
        if (child instanceof AbstractMesh && isPrimitive(child) && !isGltfNode(child)) out.push(child);
    }
    return out;
}

// =============================================================================
// Applying a matched channel
// =============================================================================

/** One Babylon animatable property a channel writes, with how to read its value. */
interface PropertyTarget {
    target: object;
    property: string;
    type: number;
    width: 1 | 3 | 4;
}

/** Visibility as an animatable number (Babylon's KHR_node_visibility setter, as a property). */
function visibilityTarget(node: TransformNode, meshes: AbstractMesh[]): object {
    return {
        set visible(value: number) {
            const on = value >= 0.5;
            for (const mesh of meshes) mesh.inheritVisibility = true;
            node.isVisible = on;
            for (const mesh of meshes) mesh.isVisible = on;
        },
        get visible(): number {
            return node.isVisible ? 1 : 0;
        },
    };
}

function trsTarget(node: TransformNode, path: TrsPath): PropertyTarget {
    if (path === "rotation") {
        if (!node.rotationQuaternion) node.rotationQuaternion = Quaternion.FromEulerVector(node.rotation);
        return { target: node, property: "rotationQuaternion", type: Animation.ANIMATIONTYPE_QUATERNION, width: 4 };
    }
    return {
        target: node,
        property: path === "translation" ? "position" : "scaling",
        type: Animation.ANIMATIONTYPE_VECTOR3,
        width: 3,
    };
}

function morphTargetsOf(primitives: readonly AbstractMesh[], j: number): PropertyTarget[] {
    const targets: PropertyTarget[] = [];
    for (const primitive of primitives) {
        const manager = (primitive as Mesh).morphTargetManager;
        if (!manager || j < 0 || j >= manager.numTargets) continue;
        const morph: MorphTarget = manager.getTarget(j);
        targets.push({ target: morph, property: "influence", type: Animation.ANIMATIONTYPE_FLOAT, width: 1 });
    }
    return targets;
}

/** Every morph target of the instance, by name: one name drives that target on every mesh that has it. */
function morphTargetsByName(view: InstanceView): Map<string, PropertyTarget[]> {
    const out = new Map<string, PropertyTarget[]>();
    const names = view.names;
    for (const [index, node] of view.nodes) {
        const mesh = names.nodeMesh[index];
        if (mesh === undefined) continue;
        const primitives = view.primitives.get(index) ?? primitivesOf(node);
        names.morphTargetNames[mesh]?.forEach((name, j) => {
            if (name === null) return;
            const list = out.get(name) ?? [];
            list.push(...morphTargetsOf(primitives, j));
            out.set(name, list);
        });
    }
    return out;
}

/**
 * Babylon keys for one channel: frame = t × fps; LINEAR, STEP, or CUBICSPLINE
 * with tangents scaled by 1/fps — exactly what Babylon's glTF loader builds
 * for the same sampler, so a bound clip samples like an inline one.
 */
function keysFor(
    sampler: DecodedSampler,
    width: 1 | 3 | 4,
    component: { index: number; count: number } | undefined,
    premultiply?: Quaternion,
): IAnimationKey[] {
    const n = sampler.input.length;
    const element = component ? component.count : width;
    const cubic = sampler.interpolation === "CUBICSPLINE";
    const out = sampler.output;
    const at = (offset: number, scale: number): unknown => {
        if (component) return out[offset + component.index] * scale;
        if (width === 1) return out[offset] * scale;
        if (width === 3) return Vector3.FromArray(out, offset).scaleInPlace(scale);
        const q = Quaternion.FromArray(out, offset).scaleInPlace(scale);
        // Left-multiplying by a constant commutes with slerp and is linear on
        // the 4-vector, so LINEAR, STEP and CUBICSPLINE keys all transform alike.
        return premultiply ? premultiply.multiply(q) : q;
    };
    const invFps = 1 / BOUND_CLIP_FPS;
    const keys: IAnimationKey[] = [];
    for (let k = 0; k < n; k++) {
        const frame = sampler.input[k] * BOUND_CLIP_FPS;
        if (cubic) {
            const base = k * element * 3;
            keys.push({ frame, inTangent: at(base, invFps), value: at(base + element, 1), outTangent: at(base + 2 * element, invFps) });
        } else {
            const key: IAnimationKey = { frame, value: at(k * element, 1) };
            if (sampler.interpolation === "STEP") key.interpolation = AnimationKeyInterpolation.STEP;
            keys.push(key);
        }
    }
    return keys;
}

function animationFor(name: string, target: PropertyTarget, keys: IAnimationKey[]): Animation {
    const animation = new Animation(name, target.property, BOUND_CLIP_FPS, target.type);
    animation.setKeys(keys, true);
    return animation;
}

// =============================================================================
// Binding
// =============================================================================

/** What one bind pass did, per source — for diagnostics and tests. */
export interface AnimationBindingReport {
    /** The source's `src`. */
    src: string;
    /** Clip names this source contributed. */
    clips: string[];
    /** Channels bound, over all its animations. */
    bound: number;
    /** Names the source's channels address that the target lacks (`Head`, `weights/tongueOut`). */
    missing: string[];
    /** Source objects with no unique name (their channels cannot bind). */
    unaddressable: string[];
    /** Object Model paths this runtime does not animate inside an instance. */
    unsupported: string[];
    /** Anything else (load failure, no instance, a root the source lacks). */
    problems: string[];
}

const reports = new WeakMap<SceneObject, AnimationBindingReport[]>();
const generations = new WeakMap<SceneObject, number>();

/** The last bind pass's report for `object` (empty when it binds nothing). */
export function getAnimationBindingReport(object: SceneObject): readonly AnimationBindingReport[] {
    return reports.get(object) ?? [];
}

function uniquePush(list: string[], value: string): void {
    if (!list.includes(value)) list.push(value);
}

const REST_RELATIVE: RestRelativeBindingData["@type"] = "RestRelativeBinding";

/**
 * The node names of the object's `RestRelativeBinding`: bound rotations on
 * them apply on top of the node's rest rotation. Read from the live behavior
 * when it is attached (a commit re-binds), else from the object's data (the
 * first bind runs before behaviors attach).
 */
function restRelativeNodes(object: SceneObject): ReadonlySet<string> {
    const live = object.getBehavior(REST_RELATIVE)?.getData() as RestRelativeBindingData | undefined;
    const data = live ?? object._getBehaviorData()?.find(b => b["@type"] === REST_RELATIVE) as RestRelativeBindingData | undefined;
    return new Set(Array.isArray(data?.nodes) ? data.nodes.filter((n): n is string => typeof n === "string") : []);
}

/**
 * Build `object`'s bound clips from its `TRACE_animation_binding` sources and
 * give them to it. Replaces (and disposes) whatever an earlier pass bound.
 * Resolves once every source has been read; a source that fails to load binds
 * nothing and is reported.
 */
export async function bindAnimationSources(object: SceneObject): Promise<void> {
    const generation = (generations.get(object) ?? 0) + 1;
    generations.set(object, generation);
    const sources = object.animationBinding?.sources ?? [];
    if (sources.length === 0) {
        if (object.boundAnimationClips.size > 0) object._setBoundAnimationClips(new Map());
        reports.delete(object);
        return;
    }

    const parsed = await Promise.all(sources.map(source =>
        loadSource(object.resources, source.src).then(
            value => ({ ok: true as const, value }),
            (error: unknown) => ({ ok: false as const, error: error instanceof Error ? error.message : String(error) }),
        )));
    // A newer pass (a reload) or a disposal overtook this one.
    if (generations.get(object) !== generation || object.transform.isDisposed()) return;

    const view = instanceView(object);
    const instance = view && !("error" in view) ? view : null;
    const ctx: BindContext = {
        instance,
        morphs: instance ? morphTargetsByName(instance) : new Map<string, PropertyTarget[]>(),
        restRelative: restRelativeNodes(object),
    };

    const clips = new Map<string, AnimationGroup>();
    const passReports: AnimationBindingReport[] = [];

    sources.forEach((source, s) => {
        const report: AnimationBindingReport = {
            src: source.src, clips: [], bound: 0, missing: [], unaddressable: [], unsupported: [], problems: [],
        };
        passReports.push(report);
        const result = parsed[s];
        if (!result.ok) {
            report.problems.push(`could not read the source: ${result.error}`);
            return;
        }
        if (view && "error" in view) report.problems.push(view.error);
        if (!instance && source.root === undefined) report.problems.push("the target has no loaded glTF instance to bind to");
        const bound = bindSourceByName(result.value.json, result.value.names, source.root);
        if (bound.rootMissing) report.problems.push(`the source has no node named "${source.root}"`);
        for (const animation of bound.animations) {
            const group = buildGroup(object, result.value, animation, report, ctx);
            if (!group) continue;
            if (animation.name === null) {
                report.problems.push(`animation ${animation.index} has no unique name and cannot be selected`);
                group.dispose();
                continue;
            }
            if (clips.has(animation.name)) {
                report.problems.push(`animation "${animation.name}" is already bound by an earlier source`);
                group.dispose();
                continue;
            }
            if (object.ownAnimationClips.has(animation.name)) {
                report.problems.push(`animation "${animation.name}" is also one of the target's own clips, which comes first`);
            }
            clips.set(animation.name, group);
            report.clips.push(animation.name);
        }
    });

    object._setBoundAnimationClips(clips);
    reports.set(object, passReports);

    for (const report of passReports) {
        // Unmatched names are normal partial binding (a performance's head
        // position binds to its Performer, not to the avatar): informational.
        if (report.missing.length) {
            Logger.debug(`[TRACE_animation_binding] "${object.name}" ← ${report.src}: no ${report.missing.map(id => `"${id}"`).join(", ")} here.`);
        }
        const parts: string[] = [];
        if (report.unaddressable.length) parts.push(`in the source, ${report.unaddressable.join("; ")}`);
        if (report.unsupported.length) parts.push(`not animatable inside an instance here: ${report.unsupported.join(", ")}`);
        parts.push(...report.problems);
        if (parts.length === 0) continue;
        Logger.warn(
            `[TRACE_animation_binding] ${ANIMATION_BINDING_UNBOUND}: "${object.name}" ← ${report.src}: `
            + `${report.bound} channel(s) bound; ${parts.join("; ")}.`,
        );
    }
}

interface BindContext {
    instance: InstanceView | null;
    /** The instance's morph targets by name, on every mesh. */
    morphs: Map<string, PropertyTarget[]>;
    restRelative: ReadonlySet<string>;
}

const IDENTITY: Record<TrsPath, number[]> = { translation: [0, 0, 0], rotation: [0, 0, 0, 1], scale: [1, 1, 1] };

/** One bound animation as an AnimationGroup, or null when none of its channels bound. */
function buildGroup(
    object: SceneObject,
    source: ParsedSource,
    animation: NameBoundAnimation,
    report: AnimationBindingReport,
    ctx: BindContext,
): AnimationGroup | null {
    const samplers = new Map<number, DecodedSampler>();
    const samplerOf = (index: number): DecodedSampler | null => {
        let decoded = samplers.get(index);
        if (!decoded) {
            try {
                decoded = readSampler(source.json, source.buffers, animation.index, index);
            } catch (error) {
                report.problems.push(`animation ${animation.index} sampler ${index}: ${error instanceof Error ? error.message : String(error)}`);
                return null;
            }
            samplers.set(index, decoded);
        }
        return decoded;
    };

    for (const dropped of animation.dropped) {
        if (dropped.reason === "unnamed") uniquePush(report.unaddressable, dropped.detail);
        else if (dropped.reason === "unsupported") uniquePush(report.unsupported, dropped.detail);
        else report.problems.push(`channel ${dropped.channel}: ${dropped.detail}`);
    }

    const name = animation.name ?? `animation${animation.index}`;
    const entries: Array<{ animation: Animation; target: object }> = [];
    let rootNode: TransformNode | null = null;
    const root = (): TransformNode => (rootNode ??= object.getOrCreateAnimationRoot());

    const add = (channel: NameChannel, target: PropertyTarget, premultiply?: Quaternion): void => {
        const sampler = samplerOf(channel.sampler);
        if (!sampler) return;
        const component = channel.kind === "morph" ? channel.component : undefined;
        entries.push({
            animation: animationFor(`${name}_channel${channel.channel}_${entries.length}`, target, keysFor(sampler, target.width, component, premultiply)),
            target: target.target,
        });
    };

    const rootAnimated = new Set<TrsPath>();
    for (const channel of animation.channels) {
        if (channel.kind === "root") {
            add(channel, trsTarget(root(), channel.path));
            rootAnimated.add(channel.path);
            report.bound++;
            continue;
        }
        // Only root channels can bind to a target with no instance.
        const instance = ctx.instance;
        if (!instance) continue;
        if (channel.kind === "morph") {
            const targets = ctx.morphs.get(channel.target);
            if (!targets?.length) {
                uniquePush(report.missing, `weights/${channel.target}`);
                continue;
            }
            for (const target of targets) add(channel, target);
            report.bound++;
            continue;
        }
        const names = instance.names;
        const index = names.nodeByName.get(channel.node);
        const node = index !== undefined ? instance.nodes.get(index) : undefined;
        if (index === undefined || !node) {
            uniquePush(report.missing, channel.node);
            continue;
        }
        if (channel.path === "visible") {
            const primitives = instance.primitives.get(index) ?? primitivesOf(node);
            add(channel, { target: visibilityTarget(node, primitives), property: "visible", type: Animation.ANIMATIONTYPE_FLOAT, width: 1 });
        } else {
            const rest = channel.path === "rotation" && ctx.restRelative.has(channel.node)
                ? Quaternion.FromArray(names.nodeRest[index].rotation)
                : undefined;
            add(channel, trsTarget(node, channel.path), rest);
        }
        report.bound++;
    }

    // Root paths this animation does not animate are identity, so no path keeps another clip's value.
    if (rootAnimated.size > 0) {
        for (const path of ["translation", "rotation", "scale"] as TrsPath[]) {
            if (rootAnimated.has(path)) continue;
            const target = trsTarget(root(), path);
            const v = target.width === 4 ? Quaternion.FromArray(IDENTITY[path]) : Vector3.FromArray(IDENTITY[path]);
            entries.push({ animation: animationFor(`${name}_rootRest_${path}`, target, [{ frame: 0, value: v }]), target: target.target });
        }
    }

    if (entries.length === 0) return null;
    const group = new AnimationGroup(name, object.babylonScene);
    for (const entry of entries) group.addTargetedAnimation(entry.animation, entry.target);
    // As Babylon's glTF loader does: the clip's time base starts at 0.
    group.normalize(0);
    return group;
}
